import * as React from "react";
import { useNavigate } from "@opendash/router";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  ChartSection,
  Card,
  InfoBox,
  PriceBox,
  SourcesSection,
  FigureCaption,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

export const PrivathaushaltPreisRoute = React.memo(
  function PrivathaushaltPreisRoute() {
    const navigate = useNavigate();
    const { t } = useTexts();

    const tabs = [
      {
        title: t("privathaushalt.tab.1.title", "Wasserverbrauch & Spartipps"),
        path: "/wasserklaerer/privathaushalt/verbrauch",
        isActive: false,
      },
      {
        title: t("privathaushalt.tab.2.title", "Der Wasserpreis"),
        path: "/wasserklaerer/privathaushalt/preis",
        isActive: true,
      },
      {
        title: t("privathaushalt.tab.3.title", "Härtegrad des Wassers"),
        path: "/wasserklaerer/privathaushalt/haerte",
        isActive: false,
      },
    ];

    // Chart für Kostenaufteilung
    const priceChartOptions = {
      chart: {
        type: "pie",
        height: 500,
      },
      title: {
        text: t(
          "privathaushalt.preis.chart.title",
          "Trinkwasserkosten aufgeteilt im Haushalt"
        ),
        style: {
          fontSize: "20px",
          fontWeight: "bold",
          color: "#2c3e50",
        },
      },
      subtitle: {
        text: t(
          "privathaushalt.preis.chart.subtitle",
          "Durchschnittliche Kostenverteilung pro Haushalt"
        ),
        style: {
          fontSize: "16px",
          color: "#666",
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          cursor: "default",
          dataLabels: {
            enabled: true,
            format: "<b>{point.name}</b>: {point.percentage:.1f}%",
          },
          showInLegend: false,
          colors: ["#1565c0", "#00897b", "#4fc3f7"],
        },
      },
      series: [
        {
          name: "Kostenanteil",
          data: [
            {
              name: t(
                "privathaushalt.preis.chart.cat1",
                "Trinkwasserbereitstellung"
              ),
              y: 30,
            },
            {
              name: t("privathaushalt.preis.chart.cat2", "Abwasserentsorgung"),
              y: 50,
            },
            {
              name: t(
                "privathaushalt.preis.chart.cat3",
                "Infrastruktur & Netz"
              ),
              y: 20,
            },
          ],
        },
      ],
      legend: {
        enabled: false,
      },
      tooltip: {
        pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
      },
      credits: { enabled: false },
    };

    return (
      <AppLayout>
        <ContentContainer>
          <h1>{t("privathaushalt.preis.title", "Der Wasserpreis")}</h1>

          <TabContainer>
            {tabs.map((tab, index) => (
              <TabButton
                key={index}
                isActive={tab.isActive}
                onClick={() => !tab.isActive && navigate(tab.path)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabContainer>

          <Card>
            <h2>
              {t(
                "privathaushalt.preis.section1.title",
                "Wie viel kostet Trinkwasser?"
              )}
            </h2>
            <p>
              {t(
                "privathaushalt.preis.section1.text1",
                "Wer Trinkwasser aus der öffentlichen Wasserversorgung bezieht, zahlt nicht nur für die Bereitstellung des Wassers selbst, sondern auch für dessen Ableitung und Behandlung in Form von Abwassergebühren. Der Gesamtpreis für einen Liter kaltes Leitungswasser beläuft sich auf etwa 0,44 Cent."
              )}
            </p>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.preis.section1.text1b",
                  "Umgerechnet bedeutet dies, dass mit einem Euro rund 230 Liter Trinkwasser aus dem Wasserhahn bezogen werden können, was etwa 1.150 Gläsern mit einem Fassungsvermögen von jeweils 0,2 Litern entspricht. Pro Person ergeben sich daraus durchschnittliche jährliche Trinkwasserkosten von rund 200 Euro. [1]"
                )
              )}
            </p>
            <PriceBox>
              <h4>{t("privathaushalt.preis.box1.title", "Wichtige Zahlen")}</h4>
              <p>
                {t(
                  "privathaushalt.preis.box1.text1",
                  "Mit einem Euro können rund 230 Liter Trinkwasser aus dem Wasserhahn bezogen werden - das entspricht etwa 1.150 Gläsern à 0,2 Litern."
                )}
              </p>
              <p>
                {t(
                  "privathaushalt.preis.box1.text2",
                  "Pro Person ergeben sich daraus durchschnittliche jährliche Trinkwasserkosten von rund 200 Euro."
                )}
              </p>
            </PriceBox>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.preis.section1.text2",
                  "Die Höhe der Wasserkosten variiert jedoch regional zum Teil erheblich. Diese Unterschiede sind unter anderem auf das jeweilige regionale Wasserdargebot sowie auf die Struktur der lokalen Wasserversorgungsunternehmen zurückzuführen. [1]"
                )
              )}
            </p>
          </Card>

          <ChartSection>
            <HighchartsReact
              highcharts={Highcharts}
              options={priceChartOptions}
            />
            <FigureCaption>
              {t(
                "privathaushalt.preis.chart.caption",
                "Abbildung 1: Trinkwasserkosten aufgeteilt im Haushalt [eigene Darstellung aus [1]]"
              )}
            </FigureCaption>
          </ChartSection>

          <Card>
            <h2>
              {t(
                "privathaushalt.preis.section2.title",
                "Wie entsteht der Wasserpreis?"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.preis.section2.intro",
                  "In Deutschland werden Wasserpreise nach dem Prinzip der Kostendeckung kalkuliert. Das bedeutet, dass die erzielten Einnahmen die laufenden Betriebs-, Infrastruktur- und Investitionskosten der Wasserversorgungsunternehmen vollständig abdecken müssen [1]:"
                )
              )}
            </p>
            <InfoBox>
              <p>
                {t(
                  "privathaushalt.preis.link1",
                  "Wenn ihr wissen wollt, was ein Wasserwerk alles übernimmt, schaut in das Kapitel 'Wasseraufbereitung'."
                )}
              </p>
            </InfoBox>
            <InfoBox>
              <p>
                {t(
                  "privathaushalt.preis.link2",
                  "Wenn ihr wissen wollt, was Klärwerke alles übernehmen, schaut in das Kapitel 'Abwasser'."
                )}
              </p>
            </InfoBox>
            <h3>
              {t(
                "privathaushalt.preis.section2.subtitle1",
                "Regionale Gegebenheiten"
              )}
            </h3>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.preis.section2.text1",
                  "Die konkrete Höhe der einzelnen Kostenbestandteile hängt dabei maßgeblich von regionalen Gegebenheiten ab [1]:"
                )
              )}
            </p>
            <ul>
              <li>
                {t("privathaushalt.preis.section2.item1", "Siedlungsdichte")}
              </li>
              <li>
                {t("privathaushalt.preis.section2.item2", "Geografische Lage")}
              </li>
              <li>
                {t(
                  "privathaushalt.preis.section2.item3",
                  "Beschaffenheit der Erdoberfläche"
                )}
              </li>
            </ul>
          </Card>

          <Card>
            <h2>
              {t(
                "privathaushalt.preis.section3.title",
                "Wie wird der Wasserverbrauch in einem Mehrfamilienhaus berechnet?"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.preis.section3.text1",
                  "Die Abrechnung des Wasserverbrauchs in Mehrfamilienhäusern wird von Mieterinnen und Mietern teilweise als problematisch wahrgenommen, da Vermieterinnen und Vermieter grundsätzlich zwischen zwei Abrechnungsmodellen wählen können. [1]"
                )
              )}
            </p>
            <h3>
              {t(
                "privathaushalt.preis.section3.subtitle1",
                "Abrechnungsmodelle"
              )}
            </h3>
            <table>
              <thead>
                <tr>
                  <th>Modell</th>
                  <th>Beschreibung</th>
                  <th>Auswirkung</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {t(
                      "privathaushalt.preis.table.model1",
                      "Verbrauchsabhängig"
                    )}
                  </td>
                  <td>
                    {t(
                      "privathaushalt.preis.table.model1.desc",
                      "Abrechnung auf Basis individuell installierter Wasserzähler in den einzelnen Wohnungen"
                    )}
                  </td>
                  <td>
                    {t(
                      "privathaushalt.preis.table.model1.impact",
                      "Gerecht: Jeder zahlt nach tatsächlichem Verbrauch"
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t("privathaushalt.preis.table.model2", "Pauschal")}</td>
                  <td>
                    {t(
                      "privathaushalt.preis.table.model2.desc",
                      "Der gesamte Wasserverbrauch wird pauschal nach Wohnfläche auf alle Mietparteien umgelegt"
                    )}
                  </td>
                  <td>
                    {t(
                      "privathaushalt.preis.table.model2.impact",
                      "Potenziell ungerecht: Ein Einpersonenhaushalt kann ähnlich viel zahlen wie ein Mehrpersonenhaushalt"
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>

          <SourcesSection>
            <h3>
              {t(
                "privathaushalt.preis.literatur.title",
                "Literaturverzeichnis"
              )}
            </h3>
            <ol>
              <li id="quelle-1">
                <LinkifyText>
                  {t(
                    "privathaushalt.preis.literatur.ref1",
                    '[1] A. Weißbach, "Durchschnittlicher Wasserverbrauch & Kosten", co2online, 2024. Zugegriffen: 12. Dezember 2025. [Online]. Verfügbar unter: https://www.co2online.de/energie-sparen/wasser-sparen/wasserverbrauch/durchschnittlicher-wasserverbrauch/'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
            </ol>
          </SourcesSection>
        </ContentContainer>
      </AppLayout>
    );
  }
);
